import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DownloadSimple, Image, VideoCamera } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import { getMediaUrl } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotoDownload = () => {
  const { photoId } = useParams();
  const [photo, setPhoto] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/photos/info/${photoId}`);
        if (r.ok) setPhoto(await r.json());
        else setError(true);
      } catch { setError(true); }
    })();
  }, [photoId]);

  const handleDownload = async () => {
    if (!photo) return;
    setDownloading(true);
    try {
      const res = await fetch(getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`));
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const ext = photo.content_type?.startsWith('video/') ? 'mp4' : 'jpg';
      link.download = `eventdrop-moment.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch { /* fallback: open in new tab */ window.open(getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`), '_blank'); }
    finally { setDownloading(false); }
  };

  const isVideo = photo?.content_type?.startsWith('video/');
  const mediaUrl = photo ? getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`) : null;

  return (
    <div className="min-h-screen bg-[#15161B] text-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="mb-6"><EventDropLogo size={40} /></div>

        {error ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Image size={32} weight="bold" className="text-gray-600" />
            </div>
            <p className="text-gray-400 text-sm">This moment is no longer available</p>
          </div>
        ) : !photo ? (
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
        ) : (
          <div className="w-full max-w-md">
            <div className="rounded-2xl overflow-hidden border border-white/10 mb-6 bg-black">
              {isVideo ? (
                <video src={mediaUrl} controls playsInline preload="auto" className="w-full" style={{ maxHeight: '60vh' }} data-testid="download-video" />
              ) : (
                <img src={mediaUrl} alt="Your moment" className="w-full" style={{ maxHeight: '60vh', objectFit: 'contain' }} data-testid="download-image" />
              )}
            </div>

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg shadow-[#B89762]/20"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="download-button"
            >
              <DownloadSimple size={24} weight="bold" />
              {downloading ? 'Downloading...' : 'Save to Phone'}
            </button>

            <p className="text-center text-xs text-gray-600 mt-4">Powered by EventDrop</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoDownload;
